import React from 'react';
import {
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from '@cowbell/ui';
import { SubmissionStatus } from '../../../api/submissions/submissions.api.types';
import { DIALOG_DROPDOWN_PROPS } from '../../../components/dialogDropdown';
import { submissionStatusLabel } from '../utils/submissionStatusLabel';
import {
  SYSTEM_STATUS_MODAL_Z,
  isReasonProvided,
} from './SystemStatusReasonModal';
import { useSystemStatusUpdate } from './useSystemStatusUpdate';

/** Every status but the one the submission has, labelled as the lists label it. */
export const statusChangeOptions = (
  current: string | undefined
): { readonly value: SubmissionStatus; readonly label: string }[] =>
  Object.values(SubmissionStatus)
    .filter((status) => status !== current)
    .map((status) => ({ value: status, label: submissionStatusLabel(status) }));

interface ChangeSubmissionStatusModalProps {
  readonly submissionId: string;
  readonly submissionNumber: string;
  readonly currentStatus: string | undefined;
  readonly onClose: () => void;
}

/** A root user's correction of a submission's status, from any status to any other, with the reason for the notes. */
export const ChangeSubmissionStatusModal = ({
  submissionId,
  submissionNumber,
  currentStatus,
  onClose,
}: ChangeSubmissionStatusModalProps) => {
  const [status, setStatus] = React.useState<SubmissionStatus | null>(null);
  const [reason, setReason] = React.useState('');
  const update = useSystemStatusUpdate({ id: submissionId, submissionNumber });
  const canSubmit =
    status !== null && isReasonProvided(reason) && !update.isLoading;

  const confirm = () => {
    if (status === null) {
      return;
    }
    update.mutate(
      { status, reason: reason.trim() },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <Modal
      opened
      onClose={update.isLoading ? () => undefined : onClose}
      closeOnEscape={!update.isLoading}
      closeOnClickOutside={!update.isLoading}
      withCloseButton={!update.isLoading}
      title={`Change status of ${submissionNumber}`}
      centered
      zIndex={SYSTEM_STATUS_MODAL_Z}
    >
      <Stack gap="md">
        <Text size="sm">
          Currently {submissionStatusLabel(currentStatus)}. The new status
          replaces every decision on the submission, and your reason is saved in
          its notes.
        </Text>
        <Select
          label="New status"
          required
          searchable
          data={statusChangeOptions(currentStatus)}
          value={status}
          onChange={(value) => setStatus(value as SubmissionStatus | null)}
          comboboxProps={DIALOG_DROPDOWN_PROPS}
        />
        <Textarea
          label="Reason"
          required
          autosize
          minRows={3}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={onClose}
            disabled={update.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={!canSubmit}
            loading={update.isLoading}
          >
            Change status
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
